import { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dialog } from 'primereact/dialog';
import { Panel } from 'primereact/panel';
import ProseMirrorComponent from '../../components/AdvancedProseMirrorEditor';
import MediaKitSectionPreview from './MediaKitSectionPreview';
import '../../components/styles.css';
import 'prosemirror-example-setup/style/style.css';
import 'prosemirror-menu/style/menu.css';

const SOCIAL_PLATFORMS = [
  { label: 'Facebook', value: 'facebook' },
  { label: 'Instagram', value: 'instagram' },
  { label: 'TikTok', value: 'tiktok' },
  { label: 'X', value: 'x' },
  { label: 'Pinterest', value: 'pinterest' },
  { label: 'LinkedIn', value: 'linkedin' },
  { label: 'Other', value: 'other' },
];

function Field({ label, value, onChange, area, rows = 3, hint }) {
  return (
    <div className="mb-3">
      <label className="block mb-1">
        <strong>{label}</strong>
      </label>
      {area ? (
        <InputTextarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          className="w-full"
          autoResize
        />
      ) : (
        <InputText value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full" />
      )}
      {hint ? <small className="block mt-1 text-color-secondary">{hint}</small> : null}
    </div>
  );
}

function LangRow({ label, es, en, onEs, onEn, area, rows }) {
  return (
    <div className="grid">
      <div className="col-12 md:col-6">
        <Field label={`${label} · Español`} value={es} onChange={onEs} area={area} rows={rows} />
      </div>
      <div className="col-12 md:col-6">
        <Field label={`${label} · English`} value={en} onChange={onEn} area={area} rows={rows} />
      </div>
    </div>
  );
}

function RowTools({ onUp, onDown, onRemove, disableUp, disableDown }) {
  return (
    <div className="flex gap-1">
      <Button type="button" icon="pi pi-arrow-up" className="p-button-text p-button-sm" disabled={disableUp} onClick={onUp} />
      <Button type="button" icon="pi pi-arrow-down" className="p-button-text p-button-sm" disabled={disableDown} onClick={onDown} />
      <Button type="button" icon="pi pi-trash" className="p-button-danger p-button-text p-button-sm" onClick={onRemove} />
    </div>
  );
}

function Card({ title, children, tools }) {
  return (
    <div className="border-1 surface-border border-round p-3 mb-3">
      <div className="flex justify-content-between align-items-center mb-3">
        <strong>{title}</strong>
        {tools}
      </div>
      {children}
    </div>
  );
}

function moveItem(list, index, dir) {
  const next = [...(list || [])];
  const target = index + dir;
  if (target < 0 || target >= next.length) return next;
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
}

function parseMetricNumber(raw) {
  const text = String(raw).replace(',', '.').trim();
  if (text === '') return '';
  if (!/^\d*\.?\d*$/.test(text)) return undefined;
  if (text === '.' || text.endsWith('.')) return text;
  const value = Number(text);
  return Number.isFinite(value) ? value : undefined;
}

function edit(setKit, recipe) {
  setKit((current) => {
    const next = JSON.parse(JSON.stringify(current));
    recipe(next);
    return next;
  });
}

const SECTION_TITLES = {
  page: 'Page',
  audience: 'Audience',
  profile: 'Profile',
  traveler: 'Traveler',
  formats: 'Formats',
  packages: 'Packages',
  process: 'Process',
  faqs: 'FAQs',
  contact: 'Contact',
};
const SECTION_ORDER = Object.keys(SECTION_TITLES);

function SectionPanel({ id, title, collapsed, onToggle, onPreview, children }) {
  return (
    <Panel
      className="mb-3"
      toggleable
      collapsed={collapsed}
      onToggle={(event) => onToggle(id, event.value)}
      expandIcon="pi pi-chevron-down"
      collapseIcon="pi pi-chevron-up"
      headerTemplate={(options) => (
        <div className={options.className} onClick={options.onTogglerClick} style={{ cursor: 'pointer' }}>
          <span className={options.titleClassName}>{title}</span>
          <div className={options.iconsClassName} onClick={(event) => event.stopPropagation()}>
            <Button
              type="button"
              label="Preview"
              icon="pi pi-eye"
              className="p-button-text p-button-sm mr-1"
              onClick={() => onPreview(id)}
            />
            {options.togglerElement}
          </div>
        </div>
      )}
    >
      {children}
    </Panel>
  );
}

function FaqEditors({ es, en, onEs, onEn }) {
  const initial = useRef({ es, en });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setReady(true), 50);
    return () => window.clearTimeout(timer);
  }, []);

  if (!ready) {
    return <div className="prosemirror-wrapper" style={{ minHeight: 160 }} />;
  }

  return (
    <div className="grid">
      <div className="col-12 md:col-6">
        <label className="block mb-1">
          <strong>Answer · Español</strong>
        </label>
        <ProseMirrorComponent content={initial.current.es || ''} onChange={onEs} />
      </div>
      <div className="col-12 md:col-6">
        <label className="block mb-1">
          <strong>Answer · English</strong>
        </label>
        <ProseMirrorComponent content={initial.current.en || ''} onChange={onEn} />
      </div>
    </div>
  );
}

export default function MediaKitForm({ kit, setKit }) {
  const { shared, es, en } = kit;
  const [openFaq, setOpenFaq] = useState(0);
  const [collapsed, setCollapsed] = useState(() =>
    Object.fromEntries(SECTION_ORDER.map((id) => [id, true]))
  );
  const [previewSection, setPreviewSection] = useState(null);
  const [previewLang, setPreviewLang] = useState('es');

  const setShared = (path, value) => {
    edit(setKit, (draft) => {
      const keys = path.split('.');
      let cursor = draft.shared;
      keys.slice(0, -1).forEach((key) => {
        cursor = cursor[key];
      });
      cursor[keys[keys.length - 1]] = value;
    });
  };

  const setBoth = (path, which, value) => {
    edit(setKit, (draft) => {
      const keys = path.split('.');
      ['es', 'en'].forEach((langKey) => {
        if (which !== 'both' && which !== langKey) return;
        let cursor = draft[langKey];
        keys.slice(0, -1).forEach((key) => {
          cursor = cursor[key];
        });
        cursor[keys[keys.length - 1]] = value;
      });
    });
  };

  const moveSharedAndLangs = (sharedKey, esList, enList, index, dir) => {
    edit(setKit, (draft) => {
      draft.shared[sharedKey] = moveItem(draft.shared[sharedKey], index, dir);
      const apply = (lang, getter) => {
        const parentKeys = getter.slice(0, -1);
        let cursor = lang;
        parentKeys.forEach((key) => {
          cursor = cursor[key];
        });
        const leaf = getter[getter.length - 1];
        cursor[leaf] = moveItem(cursor[leaf], index, dir);
      };
      apply(draft.es, esList);
      apply(draft.en, enList);
    });
  };

  const removeAt = (sharedKey, esPath, enPath, index) => {
    edit(setKit, (draft) => {
      if (sharedKey) draft.shared[sharedKey].splice(index, 1);
      const drop = (root, path) => {
        const keys = path.split('.');
        let cursor = root;
        keys.slice(0, -1).forEach((key) => {
          cursor = cursor[key];
        });
        cursor[keys[keys.length - 1]].splice(index, 1);
      };
      drop(draft.es, esPath);
      drop(draft.en, enPath);
    });
  };

  return (
    <div className="pb-8">
      <SectionPanel
        id="page"
        title="Page"
        collapsed={collapsed.page}
        onToggle={(id, next) => setCollapsed((current) => ({ ...current, [id]: next }))}
        onPreview={setPreviewSection}
      >
        <LangRow
          label="SEO title"
          es={es.seo?.title}
          en={en.seo?.title}
          onEs={(value) => setBoth('seo.title', 'es', value)}
          onEn={(value) => setBoth('seo.title', 'en', value)}
        />
        <LangRow
          label="SEO description"
          es={es.seo?.description}
          en={en.seo?.description}
          onEs={(value) => setBoth('seo.description', 'es', value)}
          onEn={(value) => setBoth('seo.description', 'en', value)}
          area
          rows={2}
        />
        <LangRow
          label="Eyebrow"
          es={es.hero?.eyebrow}
          en={en.hero?.eyebrow}
          onEs={(value) => setBoth('hero.eyebrow', 'es', value)}
          onEn={(value) => setBoth('hero.eyebrow', 'en', value)}
        />
        <LangRow
          label="Title"
          es={es.hero?.title}
          en={en.hero?.title}
          onEs={(value) => setBoth('hero.title', 'es', value)}
          onEn={(value) => setBoth('hero.title', 'en', value)}
        />
        <LangRow
          label="Description"
          es={es.hero?.description}
          en={en.hero?.description}
          onEs={(value) => setBoth('hero.description', 'es', value)}
          onEn={(value) => setBoth('hero.description', 'en', value)}
          area
        />
        <LangRow
          label="Download button"
          es={es.hero?.download_label}
          en={en.hero?.download_label}
          onEs={(value) => setBoth('hero.download_label', 'es', value)}
          onEn={(value) => setBoth('hero.download_label', 'en', value)}
        />
        <Field
          label="PDF URL"
          value={shared.pdf_url}
          onChange={(value) => setShared('pdf_url', value)}
          hint="Same file in both languages. Leave empty to open the contact email instead."
        />
        <LangRow
          label="Secondary button"
          es={es.hero?.secondary_label}
          en={en.hero?.secondary_label}
          onEs={(value) => setBoth('hero.secondary_label', 'es', value)}
          onEn={(value) => setBoth('hero.secondary_label', 'en', value)}
        />
        <LangRow
          label="Secondary anchor"
          es={es.hero?.secondary_anchor}
          en={en.hero?.secondary_anchor}
          onEs={(value) => setBoth('hero.secondary_anchor', 'es', value)}
          onEn={(value) => setBoth('hero.secondary_anchor', 'en', value)}
        />
        <LangRow
          label="Updated line"
          es={es.hero?.updated_label}
          en={en.hero?.updated_label}
          onEs={(value) => setBoth('hero.updated_label', 'es', value)}
          onEn={(value) => setBoth('hero.updated_label', 'en', value)}
        />
      </SectionPanel>

      <SectionPanel
        id="audience"
        title="Audience"
        collapsed={collapsed.audience}
        onToggle={(id, next) => setCollapsed((current) => ({ ...current, [id]: next }))}
        onPreview={setPreviewSection}
      >
        <LangRow
          label="Eyebrow"
          es={es.audience?.eyebrow}
          en={en.audience?.eyebrow}
          onEs={(value) => setBoth('audience.eyebrow', 'es', value)}
          onEn={(value) => setBoth('audience.eyebrow', 'en', value)}
        />
        <LangRow
          label="Title"
          es={es.audience?.title}
          en={en.audience?.title}
          onEs={(value) => setBoth('audience.title', 'es', value)}
          onEn={(value) => setBoth('audience.title', 'en', value)}
        />
        {(shared.metrics || []).map((metric, index) => (
          <Card
            key={`metric-${index}`}
            title={`Metric ${index + 1}`}
            tools={
              <RowTools
                disableUp={index === 0}
                disableDown={index === shared.metrics.length - 1}
                onUp={() => moveSharedAndLangs('metrics', ['audience', 'metrics'], ['audience', 'metrics'], index, -1)}
                onDown={() => moveSharedAndLangs('metrics', ['audience', 'metrics'], ['audience', 'metrics'], index, 1)}
                onRemove={() => removeAt('metrics', 'audience.metrics', 'audience.metrics', index)}
              />
            }
          >
            <Field
              label="Value"
              value={metric.value === '' || metric.value == null ? '' : String(metric.value)}
              onChange={(value) => {
                const next = parseMetricNumber(value);
                if (next === undefined) return;
                edit(setKit, (draft) => {
                  draft.shared.metrics[index].value = next;
                });
              }}
              hint="Number only. Spanish shows 2,3 and English shows 2.3."
            />
            <LangRow
              label="Unit"
              es={es.audience.metrics[index]?.unit}
              en={en.audience.metrics[index]?.unit}
              onEs={(value) => {
                edit(setKit, (draft) => {
                  draft.es.audience.metrics[index].unit = value;
                });
              }}
              onEn={(value) => {
                edit(setKit, (draft) => {
                  draft.en.audience.metrics[index].unit = value;
                });
              }}
            />
            <small className="block -mt-2 mb-3 text-color-secondary">
              Optional. Example: Millones / million, K, países / countries.
            </small>
            <LangRow
              label="Label"
              es={es.audience.metrics[index]?.label}
              en={en.audience.metrics[index]?.label}
              onEs={(value) => {
                edit(setKit, (draft) => {
                  draft.es.audience.metrics[index].label = value;
                });
              }}
              onEn={(value) => {
                edit(setKit, (draft) => {
                  draft.en.audience.metrics[index].label = value;
                });
              }}
            />
            <LangRow
              label="Note"
              es={es.audience.metrics[index]?.note}
              en={en.audience.metrics[index]?.note}
              onEs={(value) => {
                edit(setKit, (draft) => {
                  draft.es.audience.metrics[index].note = value;
                });
              }}
              onEn={(value) => {
                edit(setKit, (draft) => {
                  draft.en.audience.metrics[index].note = value;
                });
              }}
            />
          </Card>
        ))}
        <Button
          type="button"
          label="Add metric"
          icon="pi pi-plus"
          className="p-button-outlined p-button-sm"
          onClick={() => {
            edit(setKit, (draft) => {
              draft.shared.metrics.push({ value: '' });
              draft.es.audience.metrics.push({ value: '', unit: '', label: '', note: '' });
              draft.en.audience.metrics.push({ value: '', unit: '', label: '', note: '' });
            });
          }}
        />
      </SectionPanel>

      <SectionPanel
        id="profile"
        title="Profile"
        collapsed={collapsed.profile}
        onToggle={(id, next) => setCollapsed((current) => ({ ...current, [id]: next }))}
        onPreview={setPreviewSection}
      >
        <LangRow
          label="Eyebrow"
          es={es.profile?.eyebrow}
          en={en.profile?.eyebrow}
          onEs={(value) => setBoth('profile.eyebrow', 'es', value)}
          onEn={(value) => setBoth('profile.eyebrow', 'en', value)}
        />
        <LangRow
          label="Title"
          es={es.profile?.title}
          en={en.profile?.title}
          onEs={(value) => setBoth('profile.title', 'es', value)}
          onEn={(value) => setBoth('profile.title', 'en', value)}
        />
        <LangRow
          label="Countries heading"
          es={es.profile?.countries_label}
          en={en.profile?.countries_label}
          onEs={(value) => setBoth('profile.countries_label', 'es', value)}
          onEn={(value) => setBoth('profile.countries_label', 'en', value)}
        />
        {(shared.countries || []).map((row, index) => (
          <Card
            key={`country-${index}`}
            title={`Country ${index + 1}`}
            tools={
              <RowTools
                disableUp={index === 0}
                disableDown={index === shared.countries.length - 1}
                onUp={() => moveSharedAndLangs('countries', ['profile', 'countries'], ['profile', 'countries'], index, -1)}
                onDown={() => moveSharedAndLangs('countries', ['profile', 'countries'], ['profile', 'countries'], index, 1)}
                onRemove={() => removeAt('countries', 'profile.countries', 'profile.countries', index)}
              />
            }
          >
            <Field
              label="Percent"
              value={String(row.percent ?? '')}
              onChange={(value) => {
                const percent = Math.max(0, Math.min(100, Number(value) || 0));
                edit(setKit, (draft) => {
                  draft.shared.countries[index].percent = percent;
                });
              }}
              hint="0–100, same bar in both languages"
            />
            <LangRow
              label="Name"
              es={es.profile.countries[index]?.name}
              en={en.profile.countries[index]?.name}
              onEs={(value) => {
                edit(setKit, (draft) => {
                  draft.es.profile.countries[index].name = value;
                });
              }}
              onEn={(value) => {
                edit(setKit, (draft) => {
                  draft.en.profile.countries[index].name = value;
                });
              }}
            />
          </Card>
        ))}
        <Button
          type="button"
          label="Add country"
          icon="pi pi-plus"
          className="p-button-outlined p-button-sm mb-3"
          onClick={() => {
            edit(setKit, (draft) => {
              draft.shared.countries.push({ percent: 0 });
              draft.es.profile.countries.push({ name: '', percent: 0 });
              draft.en.profile.countries.push({ name: '', percent: 0 });
            });
          }}
        />
        <LangRow
          label="Cities heading"
          es={es.profile?.cities_label}
          en={en.profile?.cities_label}
          onEs={(value) => setBoth('profile.cities_label', 'es', value)}
          onEn={(value) => setBoth('profile.cities_label', 'en', value)}
        />
        {(shared.cities || []).map((row, index) => (
          <Card
            key={`city-${index}`}
            title={`City ${index + 1}`}
            tools={
              <RowTools
                disableUp={index === 0}
                disableDown={index === shared.cities.length - 1}
                onUp={() => moveSharedAndLangs('cities', ['profile', 'cities'], ['profile', 'cities'], index, -1)}
                onDown={() => moveSharedAndLangs('cities', ['profile', 'cities'], ['profile', 'cities'], index, 1)}
                onRemove={() => removeAt('cities', 'profile.cities', 'profile.cities', index)}
              />
            }
          >
            <Field
              label="Percent"
              value={String(row.percent ?? '')}
              onChange={(value) => {
                const percent = Math.max(0, Math.min(100, Number(value) || 0));
                edit(setKit, (draft) => {
                  draft.shared.cities[index].percent = percent;
                });
              }}
              hint="0–100, same bar in both languages"
            />
            <LangRow
              label="Name"
              es={es.profile.cities[index]?.name}
              en={en.profile.cities[index]?.name}
              onEs={(value) => {
                edit(setKit, (draft) => {
                  draft.es.profile.cities[index].name = value;
                });
              }}
              onEn={(value) => {
                edit(setKit, (draft) => {
                  draft.en.profile.cities[index].name = value;
                });
              }}
            />
          </Card>
        ))}
        <Button
          type="button"
          label="Add city"
          icon="pi pi-plus"
          className="p-button-outlined p-button-sm"
          onClick={() => {
            edit(setKit, (draft) => {
              draft.shared.cities.push({ percent: 0 });
              draft.es.profile.cities.push({ name: '', percent: 0 });
              draft.en.profile.cities.push({ name: '', percent: 0 });
            });
          }}
        />
      </SectionPanel>

      <SectionPanel
        id="traveler"
        title="Traveler"
        collapsed={collapsed.traveler}
        onToggle={(id, next) => setCollapsed((current) => ({ ...current, [id]: next }))}
        onPreview={setPreviewSection}
      >
        <LangRow
          label="Eyebrow"
          es={es.profile?.traveler?.eyebrow}
          en={en.profile?.traveler?.eyebrow}
          onEs={(value) => setBoth('profile.traveler.eyebrow', 'es', value)}
          onEn={(value) => setBoth('profile.traveler.eyebrow', 'en', value)}
        />
        <LangRow
          label="Description"
          es={es.profile?.traveler?.description}
          en={en.profile?.traveler?.description}
          onEs={(value) => setBoth('profile.traveler.description', 'es', value)}
          onEn={(value) => setBoth('profile.traveler.description', 'en', value)}
          area
        />
        {(es.profile?.traveler?.attributes || []).map((attr, index) => (
          <Card
            key={`attr-${index}`}
            title={`Attribute ${index + 1}`}
            tools={
              <RowTools
                disableUp={index === 0}
                disableDown={index === es.profile.traveler.attributes.length - 1}
                onUp={() => {
                  edit(setKit, (draft) => {
                    draft.es.profile.traveler.attributes = moveItem(draft.es.profile.traveler.attributes, index, -1);
                    draft.en.profile.traveler.attributes = moveItem(draft.en.profile.traveler.attributes, index, -1);
                  });
                }}
                onDown={() => {
                  edit(setKit, (draft) => {
                    draft.es.profile.traveler.attributes = moveItem(draft.es.profile.traveler.attributes, index, 1);
                    draft.en.profile.traveler.attributes = moveItem(draft.en.profile.traveler.attributes, index, 1);
                  });
                }}
                onRemove={() => removeAt(null, 'profile.traveler.attributes', 'profile.traveler.attributes', index)}
              />
            }
          >
            <LangRow
              label="Label"
              es={attr.label}
              en={en.profile.traveler.attributes[index]?.label}
              onEs={(value) => {
                edit(setKit, (draft) => {
                  draft.es.profile.traveler.attributes[index].label = value;
                });
              }}
              onEn={(value) => {
                edit(setKit, (draft) => {
                  draft.en.profile.traveler.attributes[index].label = value;
                });
              }}
            />
            <LangRow
              label="Text"
              es={attr.value}
              en={en.profile.traveler.attributes[index]?.value}
              onEs={(value) => {
                edit(setKit, (draft) => {
                  draft.es.profile.traveler.attributes[index].value = value;
                });
              }}
              onEn={(value) => {
                edit(setKit, (draft) => {
                  draft.en.profile.traveler.attributes[index].value = value;
                });
              }}
            />
          </Card>
        ))}
        <Button
          type="button"
          label="Add attribute"
          icon="pi pi-plus"
          className="p-button-outlined p-button-sm"
          onClick={() => {
            edit(setKit, (draft) => {
              draft.es.profile.traveler.attributes.push({ label: '', value: '' });
              draft.en.profile.traveler.attributes.push({ label: '', value: '' });
            });
          }}
        />
      </SectionPanel>

      <SectionPanel
        id="formats"
        title="Formats"
        collapsed={collapsed.formats}
        onToggle={(id, next) => setCollapsed((current) => ({ ...current, [id]: next }))}
        onPreview={setPreviewSection}
      >
        <LangRow
          label="Eyebrow"
          es={es.formats?.eyebrow}
          en={en.formats?.eyebrow}
          onEs={(value) => setBoth('formats.eyebrow', 'es', value)}
          onEn={(value) => setBoth('formats.eyebrow', 'en', value)}
        />
        <LangRow
          label="Title"
          es={es.formats?.title}
          en={en.formats?.title}
          onEs={(value) => setBoth('formats.title', 'es', value)}
          onEn={(value) => setBoth('formats.title', 'en', value)}
        />
        <LangRow
          label="Description"
          es={es.formats?.description}
          en={en.formats?.description}
          onEs={(value) => setBoth('formats.description', 'es', value)}
          onEn={(value) => setBoth('formats.description', 'en', value)}
          area
          rows={2}
        />
        {(shared.formats || []).map((format, index) => (
          <Card
            key={`format-${index}`}
            title={es.formats.items[index]?.title || `Format ${index + 1}`}
            tools={
              <RowTools
                disableUp={index === 0}
                disableDown={index === shared.formats.length - 1}
                onUp={() => moveSharedAndLangs('formats', ['formats', 'items'], ['formats', 'items'], index, -1)}
                onDown={() => moveSharedAndLangs('formats', ['formats', 'items'], ['formats', 'items'], index, 1)}
                onRemove={() => removeAt('formats', 'formats.items', 'formats.items', index)}
              />
            }
          >
            <div className="flex align-items-center gap-2 mb-3">
              <Checkbox
                inputId={`muted-${index}`}
                checked={Boolean(format.muted)}
                onChange={(e) => {
                  edit(setKit, (draft) => {
                    draft.shared.formats[index].muted = Boolean(e.checked);
                  });
                }}
              />
              <label htmlFor={`muted-${index}`}>
                <strong>Coming soon</strong>
              </label>
            </div>
            <LangRow
              label="Chip"
              es={es.formats.items[index]?.chip}
              en={en.formats.items[index]?.chip}
              onEs={(value) => {
                edit(setKit, (draft) => {
                  draft.es.formats.items[index].chip = value;
                });
              }}
              onEn={(value) => {
                edit(setKit, (draft) => {
                  draft.en.formats.items[index].chip = value;
                });
              }}
            />
            <LangRow
              label="Title"
              es={es.formats.items[index]?.title}
              en={en.formats.items[index]?.title}
              onEs={(value) => {
                edit(setKit, (draft) => {
                  draft.es.formats.items[index].title = value;
                });
              }}
              onEn={(value) => {
                edit(setKit, (draft) => {
                  draft.en.formats.items[index].title = value;
                });
              }}
            />
            <LangRow
              label="Description"
              es={es.formats.items[index]?.description}
              en={en.formats.items[index]?.description}
              onEs={(value) => {
                edit(setKit, (draft) => {
                  draft.es.formats.items[index].description = value;
                });
              }}
              onEn={(value) => {
                edit(setKit, (draft) => {
                  draft.en.formats.items[index].description = value;
                });
              }}
              area
              rows={2}
            />
            {(es.formats.items[index]?.specs || []).map((spec, specIndex) => (
              <div key={`spec-${index}-${specIndex}`} className="surface-100 border-round p-2 mb-2">
                <div className="flex justify-content-between align-items-center mb-2">
                  <span>Spec {specIndex + 1}</span>
                  <Button
                    type="button"
                    icon="pi pi-trash"
                    className="p-button-danger p-button-text p-button-sm"
                    onClick={() => {
                      edit(setKit, (draft) => {
                        draft.es.formats.items[index].specs.splice(specIndex, 1);
                        draft.en.formats.items[index].specs.splice(specIndex, 1);
                      });
                    }}
                  />
                </div>
                <LangRow
                  label="Label"
                  es={spec.label}
                  en={en.formats.items[index]?.specs?.[specIndex]?.label}
                  onEs={(value) => {
                    edit(setKit, (draft) => {
                      draft.es.formats.items[index].specs[specIndex].label = value;
                    });
                  }}
                  onEn={(value) => {
                    edit(setKit, (draft) => {
                      draft.en.formats.items[index].specs[specIndex].label = value;
                    });
                  }}
                />
                <LangRow
                  label="Text"
                  es={spec.value}
                  en={en.formats.items[index]?.specs?.[specIndex]?.value}
                  onEs={(value) => {
                    edit(setKit, (draft) => {
                      draft.es.formats.items[index].specs[specIndex].value = value;
                    });
                  }}
                  onEn={(value) => {
                    edit(setKit, (draft) => {
                      draft.en.formats.items[index].specs[specIndex].value = value;
                    });
                  }}
                />
              </div>
            ))}
            <Button
              type="button"
              label="Add spec"
              icon="pi pi-plus"
              className="p-button-text p-button-sm"
              onClick={() => {
                edit(setKit, (draft) => {
                  draft.es.formats.items[index].specs = draft.es.formats.items[index].specs || [];
                  draft.en.formats.items[index].specs = draft.en.formats.items[index].specs || [];
                  draft.es.formats.items[index].specs.push({ label: '', value: '' });
                  draft.en.formats.items[index].specs.push({ label: '', value: '' });
                });
              }}
            />
          </Card>
        ))}
        <Button
          type="button"
          label="Add format"
          icon="pi pi-plus"
          className="p-button-outlined p-button-sm"
          onClick={() => {
            edit(setKit, (draft) => {
              draft.shared.formats.push({ muted: false });
              const blank = { chip: '', title: '', description: '', muted: false, specs: [] };
              draft.es.formats.items.push(blank);
              draft.en.formats.items.push(JSON.parse(JSON.stringify(blank)));
            });
          }}
        />
      </SectionPanel>

      <SectionPanel
        id="packages"
        title="Packages"
        collapsed={collapsed.packages}
        onToggle={(id, next) => setCollapsed((current) => ({ ...current, [id]: next }))}
        onPreview={setPreviewSection}
      >
        <LangRow
          label="Eyebrow"
          es={es.packages?.eyebrow}
          en={en.packages?.eyebrow}
          onEs={(value) => setBoth('packages.eyebrow', 'es', value)}
          onEn={(value) => setBoth('packages.eyebrow', 'en', value)}
        />
        <LangRow
          label="Title"
          es={es.packages?.title}
          en={en.packages?.title}
          onEs={(value) => setBoth('packages.title', 'es', value)}
          onEn={(value) => setBoth('packages.title', 'en', value)}
        />
        <LangRow
          label="Description"
          es={es.packages?.description}
          en={en.packages?.description}
          onEs={(value) => setBoth('packages.description', 'es', value)}
          onEn={(value) => setBoth('packages.description', 'en', value)}
          area
          rows={2}
        />
        {(shared.packages || []).map((plan, index) => (
          <Card
            key={`plan-${index}`}
            title={es.packages.items[index]?.name || `Package ${index + 1}`}
            tools={
              <RowTools
                disableUp={index === 0}
                disableDown={index === shared.packages.length - 1}
                onUp={() => moveSharedAndLangs('packages', ['packages', 'items'], ['packages', 'items'], index, -1)}
                onDown={() => moveSharedAndLangs('packages', ['packages', 'items'], ['packages', 'items'], index, 1)}
                onRemove={() => removeAt('packages', 'packages.items', 'packages.items', index)}
              />
            }
          >
            <Field
              label="Price"
              value={plan.price}
              onChange={(value) => {
                edit(setKit, (draft) => {
                  draft.shared.packages[index].price = value;
                });
              }}
              hint="Same price in both languages. Example: $ 18,000"
            />
            <div className="flex align-items-center gap-2 mb-3">
              <Checkbox
                inputId={`badge-${index}`}
                checked={Boolean(plan.featured)}
                onChange={(e) => {
                  edit(setKit, (draft) => {
                    draft.shared.packages[index].featured = Boolean(e.checked);
                  });
                }}
              />
              <label htmlFor={`badge-${index}`}>
                <strong>Badge visible</strong>
              </label>
            </div>
            {plan.featured ? (
              <LangRow
                label="Badge text"
                es={es.packages.items[index]?.featured_label}
                en={en.packages.items[index]?.featured_label}
                onEs={(value) => {
                  edit(setKit, (draft) => {
                    draft.es.packages.items[index].featured_label = value;
                  });
                }}
                onEn={(value) => {
                  edit(setKit, (draft) => {
                    draft.en.packages.items[index].featured_label = value;
                  });
                }}
              />
            ) : null}
            <Field
              label="Button URL"
              value={plan.cta_url}
              onChange={(value) => {
                edit(setKit, (draft) => {
                  draft.shared.packages[index].cta_url = value;
                });
              }}
              hint="Optional. Empty uses the contact email."
            />
            <LangRow
              label="Name"
              es={es.packages.items[index]?.name}
              en={en.packages.items[index]?.name}
              onEs={(value) => {
                edit(setKit, (draft) => {
                  draft.es.packages.items[index].name = value;
                });
              }}
              onEn={(value) => {
                edit(setKit, (draft) => {
                  draft.en.packages.items[index].name = value;
                });
              }}
            />
            <LangRow
              label="Period"
              es={es.packages.items[index]?.period}
              en={en.packages.items[index]?.period}
              onEs={(value) => {
                edit(setKit, (draft) => {
                  draft.es.packages.items[index].period = value;
                });
              }}
              onEn={(value) => {
                edit(setKit, (draft) => {
                  draft.en.packages.items[index].period = value;
                });
              }}
            />
            <LangRow
              label="Description"
              es={es.packages.items[index]?.description}
              en={en.packages.items[index]?.description}
              onEs={(value) => {
                edit(setKit, (draft) => {
                  draft.es.packages.items[index].description = value;
                });
              }}
              onEn={(value) => {
                edit(setKit, (draft) => {
                  draft.en.packages.items[index].description = value;
                });
              }}
              area
              rows={2}
            />
            <LangRow
              label="Button label"
              es={es.packages.items[index]?.cta_label}
              en={en.packages.items[index]?.cta_label}
              onEs={(value) => {
                edit(setKit, (draft) => {
                  draft.es.packages.items[index].cta_label = value;
                });
              }}
              onEn={(value) => {
                edit(setKit, (draft) => {
                  draft.en.packages.items[index].cta_label = value;
                });
              }}
            />
            {(es.packages.items[index]?.includes || []).map((line, lineIndex) => (
              <div key={`inc-${index}-${lineIndex}`} className="surface-100 border-round p-2 mb-2">
                <div className="flex justify-content-between align-items-center mb-2">
                  <span>Includes {lineIndex + 1}</span>
                  <Button
                    type="button"
                    icon="pi pi-trash"
                    className="p-button-danger p-button-text p-button-sm"
                    onClick={() => {
                      edit(setKit, (draft) => {
                        draft.es.packages.items[index].includes.splice(lineIndex, 1);
                        draft.en.packages.items[index].includes.splice(lineIndex, 1);
                      });
                    }}
                  />
                </div>
                <LangRow
                  label="Line"
                  es={line}
                  en={en.packages.items[index]?.includes?.[lineIndex]}
                  onEs={(value) => {
                    edit(setKit, (draft) => {
                      draft.es.packages.items[index].includes[lineIndex] = value;
                    });
                  }}
                  onEn={(value) => {
                    edit(setKit, (draft) => {
                      draft.en.packages.items[index].includes[lineIndex] = value;
                    });
                  }}
                />
              </div>
            ))}
            <Button
              type="button"
              label="Add line"
              icon="pi pi-plus"
              className="p-button-text p-button-sm"
              onClick={() => {
                edit(setKit, (draft) => {
                  draft.es.packages.items[index].includes = draft.es.packages.items[index].includes || [];
                  draft.en.packages.items[index].includes = draft.en.packages.items[index].includes || [];
                  draft.es.packages.items[index].includes.push('');
                  draft.en.packages.items[index].includes.push('');
                });
              }}
            />
          </Card>
        ))}
        <Button
          type="button"
          label="Add package"
          icon="pi pi-plus"
          className="p-button-outlined p-button-sm"
          onClick={() => {
            edit(setKit, (draft) => {
              draft.shared.packages.push({ price: '', featured: false, cta_url: '' });
              const blank = {
                name: '',
                price: '',
                period: '',
                description: '',
                featured: false,
                featured_label: '',
                cta_label: '',
                cta_url: '',
                includes: [],
              };
              draft.es.packages.items.push(blank);
              draft.en.packages.items.push(JSON.parse(JSON.stringify(blank)));
            });
          }}
        />
      </SectionPanel>

      <SectionPanel
        id="process"
        title="Process"
        collapsed={collapsed.process}
        onToggle={(id, next) => setCollapsed((current) => ({ ...current, [id]: next }))}
        onPreview={setPreviewSection}
      >
        <LangRow
          label="Eyebrow"
          es={es.process?.eyebrow}
          en={en.process?.eyebrow}
          onEs={(value) => setBoth('process.eyebrow', 'es', value)}
          onEn={(value) => setBoth('process.eyebrow', 'en', value)}
        />
        <LangRow
          label="Title"
          es={es.process?.title}
          en={en.process?.title}
          onEs={(value) => setBoth('process.title', 'es', value)}
          onEn={(value) => setBoth('process.title', 'en', value)}
        />
        {(shared.steps || []).map((step, index) => (
          <Card
            key={`step-${index}`}
            title={es.process.steps[index]?.title || `Step ${index + 1}`}
            tools={
              <RowTools
                disableUp={index === 0}
                disableDown={index === shared.steps.length - 1}
                onUp={() => moveSharedAndLangs('steps', ['process', 'steps'], ['process', 'steps'], index, -1)}
                onDown={() => moveSharedAndLangs('steps', ['process', 'steps'], ['process', 'steps'], index, 1)}
                onRemove={() => removeAt('steps', 'process.steps', 'process.steps', index)}
              />
            }
          >
            <Field
              label="Number"
              value={step.number}
              onChange={(value) => {
                edit(setKit, (draft) => {
                  draft.shared.steps[index].number = value;
                });
              }}
              hint="Same number in both languages"
            />
            <LangRow
              label="Title"
              es={es.process.steps[index]?.title}
              en={en.process.steps[index]?.title}
              onEs={(value) => {
                edit(setKit, (draft) => {
                  draft.es.process.steps[index].title = value;
                });
              }}
              onEn={(value) => {
                edit(setKit, (draft) => {
                  draft.en.process.steps[index].title = value;
                });
              }}
            />
            <LangRow
              label="Description"
              es={es.process.steps[index]?.description}
              en={en.process.steps[index]?.description}
              onEs={(value) => {
                edit(setKit, (draft) => {
                  draft.es.process.steps[index].description = value;
                });
              }}
              onEn={(value) => {
                edit(setKit, (draft) => {
                  draft.en.process.steps[index].description = value;
                });
              }}
              area
              rows={2}
            />
          </Card>
        ))}
        <Button
          type="button"
          label="Add step"
          icon="pi pi-plus"
          className="p-button-outlined p-button-sm"
          onClick={() => {
            edit(setKit, (draft) => {
              draft.shared.steps.push({ number: '' });
              const blank = { number: '', title: '', description: '' };
              draft.es.process.steps.push(blank);
              draft.en.process.steps.push({ ...blank });
            });
          }}
        />
      </SectionPanel>

      <SectionPanel
        id="faqs"
        title="FAQs"
        collapsed={collapsed.faqs}
        onToggle={(id, next) => setCollapsed((current) => ({ ...current, [id]: next }))}
        onPreview={setPreviewSection}
      >
        <LangRow
          label="Eyebrow"
          es={es.faqs?.eyebrow}
          en={en.faqs?.eyebrow}
          onEs={(value) => setBoth('faqs.eyebrow', 'es', value)}
          onEn={(value) => setBoth('faqs.eyebrow', 'en', value)}
        />
        <LangRow
          label="Title"
          es={es.faqs?.h3}
          en={en.faqs?.h3}
          onEs={(value) => setBoth('faqs.h3', 'es', value)}
          onEn={(value) => setBoth('faqs.h3', 'en', value)}
        />
        <LangRow
          label="Intro"
          es={es.faqs?.p}
          en={en.faqs?.p}
          onEs={(value) => setBoth('faqs.p', 'es', value)}
          onEn={(value) => setBoth('faqs.p', 'en', value)}
          area
          rows={2}
        />
        {(es.faqs?.items || []).map((faq, index) => (
          <Card
            key={`faq-${index}`}
            title={faq.question || `FAQ ${index + 1}`}
            tools={
              <RowTools
                disableUp={index === 0}
                disableDown={index === es.faqs.items.length - 1}
                onUp={() => {
                  edit(setKit, (draft) => {
                    draft.es.faqs.items = moveItem(draft.es.faqs.items, index, -1);
                    draft.en.faqs.items = moveItem(draft.en.faqs.items, index, -1);
                  });
                  setOpenFaq(index - 1);
                }}
                onDown={() => {
                  edit(setKit, (draft) => {
                    draft.es.faqs.items = moveItem(draft.es.faqs.items, index, 1);
                    draft.en.faqs.items = moveItem(draft.en.faqs.items, index, 1);
                  });
                  setOpenFaq(index + 1);
                }}
                onRemove={() => {
                  removeAt(null, 'faqs.items', 'faqs.items', index);
                  setOpenFaq(0);
                }}
              />
            }
          >
            <LangRow
              label="Question"
              es={faq.question}
              en={en.faqs.items[index]?.question}
              onEs={(value) => {
                edit(setKit, (draft) => {
                  draft.es.faqs.items[index].question = value;
                });
              }}
              onEn={(value) => {
                edit(setKit, (draft) => {
                  draft.en.faqs.items[index].question = value;
                });
              }}
            />
            {openFaq === index ? (
              <FaqEditors
                es={faq.answer_html_updated || faq.answer_html || ''}
                en={en.faqs.items[index]?.answer_html_updated || en.faqs.items[index]?.answer_html || ''}
                onEs={(html) => {
                  edit(setKit, (draft) => {
                    draft.es.faqs.items[index].answer_html_updated = html;
                  });
                }}
                onEn={(html) => {
                  edit(setKit, (draft) => {
                    draft.en.faqs.items[index].answer_html_updated = html;
                  });
                }}
              />
            ) : (
              <Button type="button" label="Edit answers" className="p-button-text" onClick={() => setOpenFaq(index)} />
            )}
          </Card>
        ))}
        <Button
          type="button"
          label="Add FAQ"
          icon="pi pi-plus"
          className="p-button-outlined p-button-sm"
          onClick={() => {
            const nextIndex = es.faqs.items.length;
            edit(setKit, (draft) => {
              const blank = { question: '', answer: '', answer_html: '', answer_html_updated: '' };
              draft.es.faqs.items.push(blank);
              draft.en.faqs.items.push({ ...blank });
            });
            setOpenFaq(nextIndex);
          }}
        />
      </SectionPanel>

      <SectionPanel
        id="contact"
        title="Contact"
        collapsed={collapsed.contact}
        onToggle={(id, next) => setCollapsed((current) => ({ ...current, [id]: next }))}
        onPreview={setPreviewSection}
      >
        <LangRow
          label="Eyebrow"
          es={es.contact?.eyebrow}
          en={en.contact?.eyebrow}
          onEs={(value) => setBoth('contact.eyebrow', 'es', value)}
          onEn={(value) => setBoth('contact.eyebrow', 'en', value)}
        />
        <Field
          label="Email"
          value={shared.email}
          onChange={(value) => setShared('email', value)}
          hint="Same address in both languages"
        />
        <Field
          label="WhatsApp number"
          value={shared.whatsapp}
          onChange={(value) => setShared('whatsapp', value)}
          hint="Official BorderGarita line. Same number in both languages. Example: +52 686 400 2234"
        />
        <LangRow
          label="WhatsApp kicker"
          es={es.contact?.whatsapp_kicker}
          en={en.contact?.whatsapp_kicker}
          onEs={(value) => setBoth('contact.whatsapp_kicker', 'es', value)}
          onEn={(value) => setBoth('contact.whatsapp_kicker', 'en', value)}
        />
        <LangRow
          label="WhatsApp text"
          es={es.contact?.whatsapp_label}
          en={en.contact?.whatsapp_label}
          onEs={(value) => setBoth('contact.whatsapp_label', 'es', value)}
          onEn={(value) => setBoth('contact.whatsapp_label', 'en', value)}
        />
        <LangRow
          label="Email kicker"
          es={es.contact?.email_kicker}
          en={en.contact?.email_kicker}
          onEs={(value) => setBoth('contact.email_kicker', 'es', value)}
          onEn={(value) => setBoth('contact.email_kicker', 'en', value)}
        />
        <LangRow
          label="Email note"
          es={es.contact?.email_note}
          en={en.contact?.email_note}
          onEs={(value) => setBoth('contact.email_note', 'es', value)}
          onEn={(value) => setBoth('contact.email_note', 'en', value)}
        />
        <LangRow
          label="Social kicker"
          es={es.contact?.social_kicker}
          en={en.contact?.social_kicker}
          onEs={(value) => setBoth('contact.social_kicker', 'es', value)}
          onEn={(value) => setBoth('contact.social_kicker', 'en', value)}
        />
        <LangRow
          label="Social handle"
          es={es.contact?.social_handle}
          en={en.contact?.social_handle}
          onEs={(value) => setBoth('contact.social_handle', 'es', value)}
          onEn={(value) => setBoth('contact.social_handle', 'en', value)}
        />
        <LangRow
          label="Map link label"
          es={es.contact?.map_label}
          en={en.contact?.map_label}
          onEs={(value) => setBoth('contact.map_label', 'es', value)}
          onEn={(value) => setBoth('contact.map_label', 'en', value)}
        />
        {(shared.socials || []).map((social, index) => (
          <Card
            key={`social-${index}`}
            title={social.label || social.platform || `Social ${index + 1}`}
            tools={
              <RowTools
                disableUp={index === 0}
                disableDown={index === shared.socials.length - 1}
                onUp={() => moveSharedAndLangs('socials', ['contact', 'socials'], ['contact', 'socials'], index, -1)}
                onDown={() => moveSharedAndLangs('socials', ['contact', 'socials'], ['contact', 'socials'], index, 1)}
                onRemove={() => removeAt('socials', 'contact.socials', 'contact.socials', index)}
              />
            }
          >
            <div className="mb-3">
              <label className="block mb-1">
                <strong>Platform</strong>
              </label>
              <Dropdown
                value={social.platform || 'other'}
                options={SOCIAL_PLATFORMS}
                onChange={(e) => {
                  edit(setKit, (draft) => {
                    draft.shared.socials[index].platform = e.value;
                  });
                }}
                className="w-full"
              />
            </div>
            <Field
              label="URL"
              value={social.url}
              onChange={(value) => {
                edit(setKit, (draft) => {
                  draft.shared.socials[index].url = value;
                });
              }}
              hint="Same link in both languages"
            />
            <Field
              label="Name"
              value={social.label}
              onChange={(value) => {
                edit(setKit, (draft) => {
                  draft.shared.socials[index].label = value;
                });
              }}
            />
          </Card>
        ))}
        <Button
          type="button"
          label="Add social link"
          icon="pi pi-plus"
          className="p-button-outlined p-button-sm"
          onClick={() => {
            edit(setKit, (draft) => {
              const blank = { platform: 'other', label: '', url: '' };
              draft.shared.socials.push(blank);
              draft.es.contact.socials.push({ ...blank });
              draft.en.contact.socials.push({ ...blank });
            });
          }}
        />
      </SectionPanel>
      <Dialog
        header={`${SECTION_TITLES[previewSection] || 'Section'} preview`}
        visible={Boolean(previewSection)}
        onHide={() => setPreviewSection(null)}
        style={{ width: '96vw', maxWidth: 1100 }}
        maximizable
        blockScroll
        dismissableMask
        contentStyle={{
          background: '#f3f4f6',
          maxHeight: '82vh',
          overflow: 'auto',
        }}
      >
        <Dropdown
          value={previewLang}
          options={[
            { label: 'Español', value: 'es' },
            { label: 'English', value: 'en' },
          ]}
          onChange={(event) => setPreviewLang(event.value)}
          className="mb-3"
          style={{ minWidth: 180, marginTop: 12 }}
        />
        {previewSection ? (
          <MediaKitSectionPreview section={previewSection} kit={kit} lang={previewLang} />
        ) : null}
      </Dialog>
    </div>
  );
}
